import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAdminScopeUrl } from "@/hooks/use-admin-scope-url";
import { supabase } from "@/integrations/supabase/client";
import { parseMiembroFiltro, toggleMiembroFiltro } from "@/lib/scope-filtro";

/**
 * PATRÓN DE ÁMBITO PARA LISTADOS DE /admin
 * ----------------------------------------
 * Todo listado nuevo del panel debe seguir estas reglas:
 *
 * 1. UI: el ámbito se elige SOLO desde el selector global del header
 *    (<AgenciaVistaCombobox />): nivel 1 = agencia, nivel 2 = miembro.
 * 2. Columnas: cada tabla tiene una columna de agencia (`agencia_id`) y una
 *    de responsable (`asignado_a`). El servidor filtra así:
 *      - staff sin agenciaVista → sin filtro de agencia
 *      - staff con agenciaVista = uuid → .eq("agencia_id", uuid)
 *      - staff con agenciaVista = "sin_agencia" → .is("agencia_id", null)
 *      - miembro → .in("agencia_id", <sus agencias>)
 *      - miembroFiltro = uuid → .eq("asignado_a", uuid)
 *      - miembroFiltro = "uuid1,uuid2[,sin_asignar]" → varios a la vez
 *        (usar applyAsignadoFilter de src/lib/scope-filtro.ts)
 *      - miembroFiltro = "sin_asignar" → .is("asignado_a", null)
 *      - miembro sin rol owner/admin → el servidor fuerza asignado_a = userId
 * 3. queryKey: incluir SIEMPRE el ámbito para no mezclar cachés:
 *      ["<modulo>", { ambito, miembroFiltro, agenciaVista }, ...otrosFiltros]
 * 4. Columna "Asignado" con <AsignadoCell /> (reasignación rápida) y, en la
 *    vista detalle, <AsignadoHeader tabla="<tabla>" id={id} />.
 */
export type Ambito = "mios" | "agencia";

export type ScopeAgencia = {
  id: string;
  nombre: string;
  logo_url: string | null;
};

export type ScopeMembership = {
  agencia: ScopeAgencia;
  rol: string;
  userId: string;
  nombre: string | null;
};

export type DataScopeValue = {
  loading: boolean;
  userId: string | null;
  userName: string | null;
  isStaff: boolean;
  memberships: ScopeMembership[];
  agenciaActiva: ScopeAgencia | null;
  /** Rol del usuario en la agencia activa (owner/admin/editor) */
  rolEnAgencia: string | null;
  /** staff u owner/admin: puede ver datos de otros miembros */
  puedeVerAgencia: boolean;
  ambito: Ambito;
  /** Uno o varios valores separados por coma (uuid | "sin_asignar") */
  miembroFiltro: string | "sin_asignar" | null;
  /** Ids de miembros seleccionados (derivado de miembroFiltro) */
  miembrosSeleccionados: string[];
  /** ¿Incluye "sin asignar"? (derivado de miembroFiltro) */
  incluyeSinAsignar: boolean;
  agenciaVista: string | "sin_agencia" | null;
  setAmbito: (v: Ambito) => void;
  setMiembroFiltro: (v: string | "sin_asignar" | null) => void;
  /** Alterna un miembro (o "sin_asignar") en la selección múltiple */
  toggleMiembro: (v: string | "sin_asignar") => void;
  setAgenciaVista: (v: string | "sin_agencia" | null) => void;
};

export const DataScopeContext = createContext<DataScopeValue | null>(null);

const STORAGE_KEY = "bz_admin_scope";

type Stored = { miembro?: string | null; agenciaVista?: string | null };

function readStored(): Stored {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "{}") as Stored;
  } catch {
    return {};
  }
}

function readUrl() {
  if (typeof window === "undefined") return {} as Record<string, string | undefined>;
  const p = new URLSearchParams(window.location.search);
  return {
    miembro: p.get("miembro") ?? undefined,
    agencia: p.get("agencia") ?? undefined,
  };
}

export function DataScopeProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [isStaff, setIsStaff] = useState(false);
  const [memberships, setMemberships] = useState<ScopeMembership[]>([]);

  const [miembroFiltro, setMiembroFiltroState] = useState<string | "sin_asignar" | null>(
    null,
  );
  const [agenciaVista, setAgenciaVista] = useState<string | "sin_agencia" | null>(null);

  const hydrated = useRef(false);
  const defaulted = useRef(false);

  // ── Hydrate from URL (wins) then localStorage ──
  const teniaValorGuardado = useRef(false);
  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    const stored = readStored();
    const url = readUrl();
    const miembro = url.miembro ?? stored.miembro ?? null;
    if (miembro) {
      teniaValorGuardado.current = true;
      setMiembroFiltroState(miembro);
    } else if (stored.miembro === null && "miembro" in stored) {
      // El usuario eligió explícitamente "toda la agencia".
      teniaValorGuardado.current = true;
    }
    const ag = url.agencia ?? stored.agenciaVista ?? null;
    if (ag) setAgenciaVista(ag);
  }, []);


  // ── Resolve identity ──
  useEffect(() => {
    let cancelled = false;

    async function resolve(uid: string | null) {
      if (!uid) {
        if (cancelled) return;
        setUserId(null);
        setUserName(null);
        setIsStaff(false);
        setMemberships([]);
        setLoading(false);
        return;
      }
      const [rolesRes, memsRes, contactoRes] = await Promise.all([
        supabase.from("user_roles").select("role").eq("user_id", uid),
        supabase
          .from("agencia_miembros")
          .select("rol, user_id, agencias:agencia_id(id, nombre, logo_url)")
          .eq("user_id", uid),
        supabase.from("contactos").select("nombre, apellidos").eq("user_id", uid).maybeSingle(),
      ]);
      if (cancelled) return;
      const roles = (rolesRes.data ?? []).map((r) => r.role as string);
      const staff =
        roles.includes("admin") ||
        roles.includes("customer_service") ||
        roles.includes("agente");
      const displayName = contactoRes.data
        ? [contactoRes.data.nombre, contactoRes.data.apellidos].filter(Boolean).join(" ") || null
        : null;
      const mems: ScopeMembership[] = (memsRes.data ?? [])
        .filter((m) => m.agencias)
        .map((m) => ({
          agencia: m.agencias as unknown as ScopeAgencia,
          rol: m.rol as string,
          userId: m.user_id as string,
          nombre: displayName,
        }));
      setUserId(uid);
      setUserName(displayName);
      setIsStaff(staff);
      setMemberships(mems);
      setLoading(false);
    }

    supabase.auth.getSession().then(({ data }) => resolve(data.session?.user.id ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      resolve(session?.user.id ?? null);
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const agenciaActiva = useMemo(
    () => (isStaff ? null : (memberships[0]?.agencia ?? null)),
    [isStaff, memberships],
  );

  const rolEnAgencia = useMemo(() => {
    if (isStaff) return null;
    const activeId = agenciaVista ?? agenciaActiva?.id ?? null;
    const m =
      memberships.find((x) => x.agencia.id === activeId) ?? memberships[0] ?? null;
    return m?.rol ?? null;
  }, [isStaff, memberships, agenciaVista, agenciaActiva]);

  const puedeVerAgencia =
    isStaff || rolEnAgencia === "owner" || rolEnAgencia === "admin";

  // ── Ámbito por defecto de los usuarios de agencia ──
  // Editor: siempre solo sus datos.
  // Owner/admin de agencia: por defecto "solo mis datos" (puede ampliarlo).
  useEffect(() => {
    if (loading || !userId) return;
    if (!puedeVerAgencia) {
      if (miembroFiltro !== userId) setMiembroFiltroState(userId);
      return;
    }
    if (defaulted.current) return;
    defaulted.current = true;
    if (!isStaff && !teniaValorGuardado.current && miembroFiltro === null) {
      setMiembroFiltroState(userId);
    }
  }, [loading, userId, isStaff, puedeVerAgencia, miembroFiltro]);


  // ── Persist ──
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ miembro: miembroFiltro, agenciaVista }),
      );
    } catch {
      /* ignore */
    }
  }, [miembroFiltro, agenciaVista]);

  useAdminScopeUrl(miembroFiltro, agenciaVista, !loading);

  const ambito: Ambito =
    miembroFiltro && userId && miembroFiltro === userId ? "mios" : "agencia";

  const setMiembroFiltro = useCallback(
    (v: string | "sin_asignar" | null) => setMiembroFiltroState(v),
    [],
  );

  const setAmbito = useCallback(
    (v: Ambito) => setMiembroFiltroState(v === "mios" ? userId : null),
    [userId],
  );

  const toggleMiembro = useCallback(
    (v: string | "sin_asignar") =>
      setMiembroFiltroState((cur) => toggleMiembroFiltro(cur, v)),
    [],
  );

  const { ids: miembrosSeleccionados, sinAsignar: incluyeSinAsignar } = useMemo(
    () => parseMiembroFiltro(miembroFiltro),
    [miembroFiltro],
  );

  const value = useMemo<DataScopeValue>(
    () => ({
      loading,
      userId,
      userName,
      isStaff,
      memberships,
      agenciaActiva,
      rolEnAgencia,
      puedeVerAgencia,
      ambito,
      miembroFiltro,
      miembrosSeleccionados,
      incluyeSinAsignar,
      agenciaVista,
      setAmbito,
      setMiembroFiltro,
      toggleMiembro,
      setAgenciaVista,
    }),
    [
      loading,
      userId,
      userName,
      isStaff,
      memberships,
      agenciaActiva,
      rolEnAgencia,
      puedeVerAgencia,
      ambito,
      miembroFiltro,
      miembrosSeleccionados,
      incluyeSinAsignar,
      agenciaVista,
      setAmbito,
      setMiembroFiltro,
      toggleMiembro,
    ],
  );

  return <DataScopeContext.Provider value={value}>{children}</DataScopeContext.Provider>;
}
